import { Editor, Element as SlateElement } from 'slate'

import { IFileSettings } from 'core-components/library/dragNDropFileUploader'

import { getImageSettings, MAX_FILE_SIZE } from '../helpers/imageHelper'
import { insertDataFromOtherEditors, insertDataWithHtmlParsing } from '../helpers/insertDataHelper'
import { insertImage } from '../helpers/slateHelper'
import { isImageUrl } from '../helpers/urlHelper'
import { BLOCK_FORMATS, CustomEditor } from '../interface/custom-types'

export const withImages = (editor: CustomEditor, onLoad: (isLoading: boolean) => void) => {
  const { insertData, isVoid } = editor

  editor.isVoid = (element) => {
    return element.type === BLOCK_FORMATS.image ? true : isVoid(element)
  }

  editor.insertData = async (data) => {
    const text = data.getData('text/plain')
    const { files } = data

    if (files && files.length > 0) {
      if (editor.fileUploadAdapter) {
        for (const file of files) {
          if (file.size / 1024 / 1024 >= MAX_FILE_SIZE) {
            return
          }
          const reader = new FileReader()
          const [mime] = file.type.split('/')

          if (mime === 'image') {
            await new Promise<void>(async (resolve) => {
              reader.onloadend = (e: any) => {
                const fileDetail: IFileSettings = { file: file, value: e.target.result }
                onLoad(true)
                editor.fileUploadAdapter!(fileDetail).then((url) => {
                  insertImage(editor, getImageSettings({ url: url.toString() }))
                  onLoad(false)
                  resolve()
                })
              }
              reader.readAsDataURL(file)
            })
          }
        }
      }
    } else if (isImageUrl(text)) {
      insertImage(editor, getImageSettings({ url: text }))
    } else {
      // check if the received value is from RTE or from Mac editors,
      // the text copied from mac editors has a special structure in html,
      // so it makes no sense to send it for processing in parseHtmlToValue
      // It also makes no sense to send elements that do not have data of the 'text/html' type.
      // this will turn the text into an empty string
      if (data.getData('application/x-slate-fragment')) {
        // If we are inside an expand block, then we insert only text so as not to insert the expand block again
        const node = Editor.above(editor, {
          match: (n) => SlateElement.isElement(n) && n.type === BLOCK_FORMATS.expandBlock,
        })
        if (node) {
          insertDataWithHtmlParsing(editor, data.getData('text/plain'))
        } else {
          insertData(data)
        }
      } else if (data.getData('text/html') && !data.types.includes('text/rtf')) {
        insertDataWithHtmlParsing(editor, data.getData('text/html'))
      } else if (data.getData('text/rtf')) {
        // data that contains rtf in types has another html, which requires
        // preliminary preparation before sending it for deserialization
        insertDataFromOtherEditors(editor, data.getData('text/html'))
      } else {
        insertData(data)
      }
    }
  }

  return editor
}
